import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import PropTypes from 'prop-types';
import { FundType } from '../../../constants';

const Img = styled('img')({
  position: 'relative',
  maxWidth: '80px',
  maxHeight: '80px',
  zIndex: 2,
  width: 'auto',
  height: 'auto',
});

const FundFamilyContainer = styled('label')(({ theme, selected }) => ({
  position: 'relative',
  borderRadius: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: selected ? '#f5f0f9' : 'transparent',
  border: selected ? `2px solid ${theme.palette.secondary.main}` : 'none',
  cursor: 'pointer',
  height: '210px',
  [theme.breakpoints.down('md')]: {
    height: '250px',
  },
  [theme.breakpoints.down('sm')]: {
    height: '210px',
  },
  '&:hover': {
    backgroundColor: '#EDE7F6',
  },
}));

const InputBg = styled(Box)(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  margin: theme.spacing(1),
  padding: theme.spacing(1, 0),
  borderRadius: 4,
  [theme.breakpoints.up('sm')]: {
    margin: theme.spacing(1),
    padding: theme.spacing(2, 0),
  },
}));

const TypeFamilyDetails = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  textAlign: 'center',
});

export const FundTitleTypography = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  '&&': {
    color: '#7b5599',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 14,
  },
}));

export const FundSubtitleTypography = styled(Typography)({
  fontSize: 14,
  fontWeight: 400,
  fontFamily: 'Inter,sans-serif',
});

const FundFamily = ({
  family, fundType, selectorState = true, onClick,
}) => {
  const theme = useTheme();
  return (
    <FundFamilyContainer onClick={onClick} selected={selectorState}>
      <InputBg>
        <FundFamilyDisplay family={family} fundType={fundType} />
      </InputBg>
      {selectorState && (
        <span
          style={{
            position: 'absolute',
            top: '-2%',
            right: '-2%',
            color: theme.palette.secondary.main,
            fontSize: 24,
            borderRadius: '50%',
            width: 18,
            height: 18,
            backgroundColor: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CheckCircleIcon />
        </span>
      )}
    </FundFamilyContainer>
  );
};

const FundFamilyDisplay = ({ family, fundType }) => (
  <TypeFamilyDetails>
    <FundLogo
      logo={family.fields?.logo}
      description={family.fields?.fundFamilyDisplayName}
    />
    <Box>
      <FundTitleTypography>{family.fields?.fundFamilyDisplayName}</FundTitleTypography>
      <FundSubtitleTypography>
        { fundType === FundType.FOCUSED ? family.fields?.subTitle : family.general?.title }
      </FundSubtitleTypography>
    </Box>
  </TypeFamilyDetails>
);

const FundLogo = ({ logo, description }) => {
  const url = logo?.url ? logo.url : null;
  return url ? (
    <Img
      src={url}
      width={logo?.dimensions.width}
      height={logo?.dimensions.height}
      alt={description}
      sizes="(min-width: 40em) 8rem, 4rem"
    />
  ) : null;
};

FundFamily.propTypes = {
  family: PropTypes.shape({
    general: PropTypes.shape({
      title: PropTypes.string.isRequired,
    }),
    fields: PropTypes.shape({
      logo: PropTypes.shape({
        url: PropTypes.string,
        dimensions: PropTypes.shape({
          width: PropTypes.number,
          height: PropTypes.number,
        }),
      }),
      subTitle: PropTypes.string,
    }).isRequired,
  }).isRequired,
  selectorState: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  fundType: PropTypes.number.isRequired,
};

FundFamilyDisplay.propTypes = {
  family: PropTypes.shape({
    general: PropTypes.shape({
      title: PropTypes.string.isRequired,
    }),
    fields: PropTypes.shape({
      logo: PropTypes.shape({
        url: PropTypes.string,
        dimensions: PropTypes.shape({
          width: PropTypes.number,
          height: PropTypes.number,
        }),
      }),
      subTitle: PropTypes.string,
      fundFamilyDisplayName: PropTypes.string,
    }),
  }),
  fundType: PropTypes.number.isRequired,
};

FundLogo.propTypes = {
  logo: PropTypes.shape({
    url: PropTypes.string,
    dimensions: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),
  }),
  description: PropTypes.string,
};

export default FundFamily;
